import { useState, useEffect } from "react";
import { X, Copy, Share2, Check, ExternalLink, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  chatId: string;
  chatTitle: string;
  isDarkMode: boolean;
}

export function ShareDialog({
  isOpen,
  onClose,
  chatId,
  chatTitle,
  isDarkMode,
}: ShareDialogProps) {
  const [shareLink, setShareLink] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [linkDate, setLinkDate] = useState<string>("");
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (isOpen && chatId) {
      loadOrGenerateShareLink();
    }
  }, [isOpen, chatId]);

  // Lock body scroll when dialog is open
  useEffect(() => {
    if (isOpen) {
      // Lock scroll on body
      const originalStyle = window.getComputedStyle(document.body).overflow;
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.style.top = `-${window.scrollY}px`;

      return () => {
        // Restore scroll
        const scrollY = document.body.style.top;
        document.body.style.overflow = originalStyle;
        document.body.style.position = '';
        document.body.style.width = '';
        document.body.style.top = '';
        window.scrollTo(0, parseInt(scrollY || '0') * -1);
      };
    }
  }, [isOpen]);

  const loadOrGenerateShareLink = async () => {
    setIsLoading(true);
    try {
      // Check if a share link already exists
      const { data: existingSession, error: fetchError } = await supabase
        .from("chat_sessions")
        .select("shared_link, is_shared, share_date")
        .eq("id", chatId)
        .single();

      if (fetchError) throw fetchError;

      // If is_shared is true and link exists, show it
      if (existingSession?.is_shared && existingSession?.shared_link) {
        const fullLink = `https://oreog.netlify.app/s/${existingSession.shared_link}`;
        setShareLink(fullLink);
        setLinkDate(existingSession.share_date);
      } else {
        // No existing link - automatically generate a new one
        await generateNewShareLink();
      }
    } catch (error) {
      console.error("Error loading share link:", error);
      toast({
        title: "Error",
        description: "Failed to load share link",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const generateNewShareLink = async () => {
    setIsLoading(true);
    try {
      // Call the database function to generate a unique link
      const { data: linkData, error: linkError } = await supabase.rpc(
        "generate_share_link"
      );

      if (linkError) throw linkError;

      const uniqueLink = linkData as string;
      const now = new Date().toISOString();

      // Update the session with the new share link
      const { error: updateError } = await supabase
        .from("chat_sessions")
        .update({
          shared_link: uniqueLink,
          is_shared: true,
          share_date: now,
        })
        .eq("id", chatId);

      if (updateError) throw updateError;

      const fullLink = `https://oreog.netlify.app/s/${uniqueLink}`;
      setShareLink(fullLink);
      setLinkDate(now);

      toast({
        title: shareLink ? "Link Updated" : "Link Generated",
        description: shareLink ? "Your share link has been updated!" : "Your chat is now shareable!",
      });
    } catch (error) {
      console.error("Error generating share link:", error);
      toast({
        title: "Error",
        description: "Failed to generate share link",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLink = async () => {
    setIsLoading(true);
    try {
      // Remove share link from session
      const { error: updateError } = await supabase
        .from("chat_sessions")
        .update({
          shared_link: null,
          is_shared: false,
          share_date: null,
        })
        .eq("id", chatId);

      if (updateError) throw updateError;

      setShareLink("");
      setLinkDate("");
      setShowDeleteConfirm(false);

      toast({
        title: "Link Deleted",
        description: "Share link has been removed",
      });
    } catch (error) {
      console.error("Error deleting share link:", error);
      toast({
        title: "Error",
        description: "Failed to delete share link",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error("Failed to copy:", error);
      toast({
        title: "Error",
        description: "Failed to copy link",
        variant: "destructive",
      });
    }
  };

  const handleShareToDevice = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: chatTitle,
          text: `Check out this conversation: ${chatTitle}`,
          url: shareLink,
        });
      } else {
        toast({
          title: "Not Supported",
          description: "Sharing is not supported on this device",
          variant: "destructive",
        });
      }
    } catch (error) {
      // User cancelled or error occurred
      console.error("Share failed:", error);
    }
  };

  const handleRegenerateLink = async () => {
    setIsLoading(true);
    await generateNewShareLink();
    setIsLoading(false);
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
        onClick={onClose}
        onTouchMove={(e) => e.preventDefault()}
      />

      {/* Dialog */}
      <div className="fixed inset-0 z-[101] flex items-center justify-center px-1 sm:px-4 py-4">
        <div
          className={`relative w-full max-w-md rounded-2xl border px-3 sm:px-6 py-6 shadow-2xl ${
            isDarkMode
              ? "bg-[#1c1c1c] border-[#2e2e2e] text-white"
              : "bg-white border-gray-200 text-gray-900"
          }`}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold flex items-center gap-2">
              {chatTitle}
            </h2>
            <button
              onClick={onClose}
              className={`p-2 rounded-lg transition-colors ${
                isDarkMode
                  ? "hover:bg-[#2a2a2a] text-gray-400"
                  : "hover:bg-gray-100 text-gray-600"
              }`}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          ) : shareLink && (
            <>
              {/* Link Display with Date */}
              <div className="mb-4">
                <div
                  className={`p-2 sm:p-3 rounded-xl flex items-center justify-between gap-3 ${
                    isDarkMode
                      ? "bg-[#2a2a2a]"
                      : "bg-gray-50"
                  }`}
                >
                  <p
                    className={`text-sm break-all select-text flex-1 ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    {shareLink}
                  </p>
                  <button
                    onClick={handleCopyLink}
                    className={`flex-shrink-0 p-2 rounded-lg transition-colors ${
                      isDarkMode
                        ? "hover:bg-[#3a3a3a] text-gray-400 hover:text-white"
                        : "hover:bg-gray-200 text-gray-600 hover:text-gray-900"
                    }`}
                    title="Copy link"
                  >
                    {isCopied ? (
                      <Check className="w-4 h-4 text-green-500" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                </div>
                {linkDate && (
                  <p
                    className={`text-xs mt-2 ${
                      isDarkMode ? "text-gray-500" : "text-gray-600"
                    }`}
                  >
                    {shareLink.includes("https://oreog.netlify.app") ? "Last updated" : "Created"}: {new Date(linkDate).toLocaleString()}
                  </p>
                )}
              </div>

              {/* Action Buttons Row */}
              <div className="flex gap-2 mb-3">
                {navigator.share && (
                  <button
                    onClick={handleShareToDevice}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium border transition-colors ${
                      isDarkMode
                        ? "border-[#3a3a3a] hover:bg-[#2a2a2a] text-gray-300"
                        : "border-gray-300 hover:bg-gray-50 text-gray-700"
                    }`}
                  >
                    <Share2 className="w-4 h-4" />
                    Share
                  </button>
                )}

                

                <button
                  onClick={() => setShowDeleteConfirm(true)}
                  className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium border transition-colors ${
                    isDarkMode
                      ? "border-red-500/30 hover:bg-red-500/10 text-red-400"
                      : "border-red-200 hover:bg-red-50 text-red-600"
                  }`}
                >
                  <Trash2 className="w-4 h-4" />
                  Delete
                </button>
              </div>

              {/* New Link Button */}
              {/* <button
                onClick={generateNewShareLink}
                className={`w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl font-medium border transition-colors ${
                  isDarkMode
                    ? "border-[#3a3a3a] hover:bg-[#2a2a2a] text-gray-400"
                    : "border-gray-300 hover:bg-gray-50 text-gray-600"
                }`}
              >
                <ExternalLink className="w-4 h-4" />
                Generate New Link
              </button> */}

              {/* Warning Message */}
              <div
                className={`mt-4 p-3 rounded-xl border-l-4 ${
                  isDarkMode
                    ? "bg-yellow-500/10 border-yellow-500"
                    : "bg-yellow-50 border-yellow-400"
                }`}
              >
                <p
                  className={`text-xs ${
                    isDarkMode ? "text-yellow-200" : "text-yellow-800"
                  }`}
                >
                  Anyone with this link can view the conversation as it was when the link was created.
                </p>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-[102] flex items-center justify-center">
          <div 
            className="absolute inset-0 bg-black/50" 
            onClick={() => setShowDeleteConfirm(false)} 
          />
          <div 
            className={`relative z-[103] w-[92%] max-w-md rounded-2xl border p-5 shadow-2xl ${
              isDarkMode 
                ? 'bg-[#1c1c1c] border-[#2a2a2a] text-white' 
                : 'bg-white border-gray-200 text-gray-900'
            }`}
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">Delete Share Link</h3>
            <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'} mb-4`}>
              Are you sure you want to delete this share link? The link will stop working and cannot be recovered.
            </p>
            <div className="flex justify-end gap-2">
              <button
                className={`px-3 py-2 rounded-full ${
                  isDarkMode 
                    ? 'bg-transparent hover:bg-[#2a2a2a] text-gray-300' 
                    : 'bg-transparent hover:bg-gray-100 text-gray-700'
                }`}
                onClick={() => setShowDeleteConfirm(false)}
              >
                Cancel
              </button>
              <button
                className={`px-4 py-2 rounded-full ${
                  isDarkMode 
                    ? 'bg-red-600 hover:bg-red-700 text-white' 
                    : 'bg-red-600 hover:bg-red-700 text-white'
                }`}
                onClick={handleDeleteLink}
                disabled={isLoading}
              >
                {isLoading ? "Deleting..." : "Yes, delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
